import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "../lib/api";
import { Badge, Banner, Button, Card, Checkbox, EmptyState, Field, Mono, PageBody, PageHeader, StackedList } from "../ui/Field";

type Webhook = { id: string; name: string; url: string; events: string; active: number; created_at: number };

export default function Webhooks() {
  const { slug } = useParams();
  const [items, setItems] = useState<Webhook[]>([]);
  const [available, setAvailable] = useState<string[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [showNew, setShowNew] = useState(false);
  const [name, setName] = useState("");
  const [url, setUrl] = useState("");
  const [secret, setSecret] = useState("");
  const [selectedEvents, setSelectedEvents] = useState<string[]>([]);

  const load = () => api.get<{ items: Webhook[]; available_events: string[] }>(`/api/sites/${slug}/webhooks`)
    .then((r) => { setItems(r.items); setAvailable(r.available_events); })
    .catch((e) => setErr((e as Error).message));
  useEffect(() => { if (slug) load(); }, [slug]);

  async function create(e: React.FormEvent) {
    e.preventDefault();
    try {
      await api.post(`/api/sites/${slug}/webhooks`, { name, url, secret: secret || undefined, events: selectedEvents });
      setShowNew(false); setName(""); setUrl(""); setSecret(""); setSelectedEvents([]);
      load();
    } catch (e) { setErr((e as Error).message); }
  }
  async function remove(id: string) {
    if (!confirm("Delete this webhook?")) return;
    try { await api.delete(`/api/sites/${slug}/webhooks/${id}`); load(); }
    catch (e) { setErr((e as Error).message); }
  }

  return (
    <PageBody>
      <PageHeader
        eyebrow="№ 12 · Outgoing events"
        title={<>Webhooks</>}
        description={<>HTTP callbacks fired on CMS events. Signed with <Mono>x-cms-signature: sha256=…</Mono> when a secret is set.</>}
        actions={<Button onClick={() => setShowNew((s) => !s)}>{showNew ? "Cancel" : "+ Webhook"}</Button>}
      />
      {err && <Banner kind="error">{err}</Banner>}

      {showNew && (
        <Card number="W0" title="New webhook" className="mb-6">
          <form onSubmit={create}>
            <Field label="Name" required value={name} onChange={(e) => setName(e.target.value)} placeholder="Slack notifier" />
            <Field label="URL" type="url" required value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://hooks.slack.com/…" />
            <Field label="Signing secret (optional)" value={secret} onChange={(e) => setSecret(e.target.value)} help="If set, requests carry x-cms-signature: sha256=…" />
            <div className="mb-4">
              <span className="label-tracked block mb-2">Events</span>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-1">
                {available.map((ev) => (
                  <Checkbox
                    key={ev}
                    checked={selectedEvents.includes(ev)}
                    onChange={(e) => setSelectedEvents((xs) => e.target.checked ? [...xs, ev] : xs.filter((x) => x !== ev))}
                    label={<Mono>{ev}</Mono>}
                  />
                ))}
              </div>
            </div>
            <Button type="submit" disabled={selectedEvents.length === 0}>Create webhook</Button>
          </form>
        </Card>
      )}

      <Card number="W1" title={`Configured (${items.length})`} bodyClassName={items.length === 0 ? "p-5" : "p-0"}>
        {items.length === 0 ? (
          <EmptyState title="No webhooks yet." description="Add one above to fan events out to Slack, Discord, n8n, or any HTTP endpoint." />
        ) : (
          <StackedList className="px-5">
            {items.map((w) => (
              <li key={w.id} className="flex items-start justify-between gap-4 py-3.5">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-[14px] text-[var(--color-ink)] font-medium">{w.name}</span>
                    {w.active ? <Badge tone="success">active</Badge> : <Badge tone="warning">inactive</Badge>}
                  </div>
                  <Mono className="text-[11.5px] break-all block">{w.url}</Mono>
                  <div className="text-[11px] text-[var(--color-muted)] mt-1 font-mono">
                    {(JSON.parse(w.events) as string[]).map((e) => <span key={e} className="mr-1.5">{e}</span>)}
                  </div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => remove(w.id)}>Delete</Button>
              </li>
            ))}
          </StackedList>
        )}
      </Card>
    </PageBody>
  );
}
